import React, { FC, memo, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { RoutedTabPanels } from "router";

import { Section } from "@/components/Section";
import { ITabOption, TabSections, TabSelect, TTabOption } from "@/components/Tabs";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Col, Flex, Row, Tabs } from "@/eworkitComponents";
import { Container } from "@/views/components/Container";

import { PageHeader } from "../../components/PageHeader";
import { CloseAccountSettings } from "./UserAccount/Settings/CloseAccountSettings";
import { ConvertProfileSettings } from "./UserAccount/Settings/ConvertProfileSettings";
import { EmailSettings } from "./UserAccount/Settings/EmailSettings";
import { PasswordSettings } from "./UserAccount/Settings/PasswordSettings";
import { PlatformNotificationsSettings } from "./UserAccount/Settings/PlatformNotificationSettings";
import { SystemAppearanceSettings } from "./UserAccount/Settings/SystemAppearanceSettings";
import { UserInformationSettings } from "./UserAccount/Settings/UserInformationSettings";

export const UserSettings: FC = memo(() => {
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;
  const isManager = User.hasCompanyManagerPrivileges();

  const { t } = useTranslation("session");

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const tabOptions: TTabOption[] = useMemo(() => {
    const options: ITabOption[] = [
      {
        type: "option",
        value: "user-information",
        label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.USER_INFORMATION"),
      },
      { type: "option", value: "email-address", label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.EMAIL_ADDRESS") },
      {
        type: "option",
        value: "platform notifications",
        label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.NOTIFICATIONS"),
      },
      { type: "option", value: "system-appearance", label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.APPEARANCE") },
      { type: "option", value: "password", label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.PASSWORD") },
    ];

    if (!companyId) {
      options.push({
        type: "option",
        value: "convert-profile",
        label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.CONVERT_PROFILE_TO_COMPANY_PROFILE"),
      });
    }

    options.push({
      type: "option",
      value: "close-account",
      label: t("USERS.SETTINGS.USER_SETTINGS.TAB_NAME.CLOSE_ACCOUNT"),
    });

    return [
      {
        type: "group",
        options,
      },
    ];
  }, [t, isManager, companyId]);

  const tabPanels = useMemo(() => {
    const panels: { [key: string]: () => JSX.Element } = {
      "user-information": () => <UserInformationSettings />,
      "email-address": () => <EmailSettings />,
      "platform notifications": () => <PlatformNotificationsSettings />,
      "system-appearance": () => <SystemAppearanceSettings />,
      password: () => <PasswordSettings />,
      "close-account": () => <CloseAccountSettings />,
    };

    if (!companyId) {
      panels["convert-profile"] = () => <ConvertProfileSettings />;
    }

    return panels;
  }, [companyId]);

  return (
    <>
      {<PageHeader header={t("USERS.SETTINGS.USER_SETTINGS.PAGE_HEADER")} />}
      <Container padding={isSmallScreen ? ["sm", "sm", "md"] : ["md", "md", "xxl", "md"]}>
        <Row size="md" direction={isSmallScreen ? "column" : "row"}>
          <Col width={{ xxs: 12, sm: 4 }}>
            <Flex direction="column" innerMargin="md">
              {isSmallScreen ? (
                <Section padding={["lg"]} innerMargin="md">
                  <TabSelect pathName="user.settings" routeParam="tab" options={tabOptions} />
                </Section>
              ) : (
                <TabSections
                  pathName="user.settings"
                  routeParam="tab"
                  options={tabOptions}
                  variant="vertical-icon-tab"
                  innerMargin="sm"
                />
              )}
            </Flex>
          </Col>

          <Col width={{ xxs: 12, sm: 8 }}>
            <Tabs>
              <RoutedTabPanels routeParam="tab" views={tabPanels} />
            </Tabs>
          </Col>
        </Row>
      </Container>
    </>
  );
});
